import React from 'react';
import {useParams,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import californiaLocations from '../data/californiaLocations';
import { trackButtonClick } from '../utils/tracking';

const {FiMapPin,FiShield,FiDollarSign,FiClock,FiCheck,FiStar,FiArrowLeft}=FiIcons;

const SubCityLandingPage=()=> {
  const {citySlug,subCitySlug}=useParams();
  const cityData=californiaLocations[citySlug];
  const subCityData=cityData?.subCities?.find(sub=> sub.slug===subCitySlug);

  if (!cityData || !subCityData) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Location Not Found</h1>
          <p className="text-gray-600 mb-8">The location you're looking for doesn't exist in our coverage area.</p>
          <Link
            to="/"
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const benefits=[
    {icon: FiShield,title: `${subCityData.name} Pet Protection`,description: `Comprehensive coverage for ${subCityData.name} pets with up to 90% reimbursement`},
    {icon: FiDollarSign,title: 'Local Pricing',description: `Affordable plans for ${subCityData.name} residents starting at $${Math.round(cityData.averageCosts.dog * 0.95)}/month`},
    {icon: FiClock,title: 'Fast Local Service',description: `Quick claims processing with local ${cityData.name} area support`}
  ];

  return (
    <div className="overflow-hidden">
      {/* Breadcrumb */}
      <section className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span>›</span>
            <Link to={`/pet-insurance/${citySlug}`} className="hover:text-blue-600">
              Pet Insurance {cityData.name}
            </Link>
            <span>›</span>
            <span className="text-gray-900">Pet Insurance {subCityData.name}</span>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center space-x-2 mb-4">
                <SafeIcon icon={FiMapPin} className="h-5 w-5 text-blue-300" />
                <span className="text-blue-200">Serving {subCityData.name} & {cityData.name} Area</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                Pet Insurance {subCityData.name} CA
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Protect your {subCityData.name} pets with America's #1 pet insurance. Local coverage near {subCityData.landmarks.slice(0,2).join(' and ')}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick(`${subCityData.name} Hero Get Quote`)}
                  className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors text-center"
                >
                  Get {subCityData.name} Quote
                </a>
                <Link
                  to="/compare"
                  onClick={() => trackButtonClick(`${subCityData.name} Hero Compare Plans`)}
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-colors text-center"
                >
                  Compare Plans
                </Link>
              </div>
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">${Math.round(cityData.averageCosts.dog * 0.95)}</div>
                  <div className="text-sm text-blue-200">Dog Plans From</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">${Math.round(cityData.averageCosts.cat * 0.95)}</div>
                  <div className="text-sm text-blue-200">Cat Plans From</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold">90%</div>
                  <div className="text-sm text-blue-200">Reimbursement</div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=600&h=400&fit=crop&crop=center"
                alt={`Happy pet family in ${subCityData.name},California with Nationwide Pet Insurance`}
                className="rounded-lg shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-gray-900 p-4 rounded-lg shadow-lg">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500" />
                  <span className="font-semibold">Protected in {subCityData.name}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why {subCityData.name} Pet Owners Choose Nationwide
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From {subCityData.landmarks[0]} to the greater {cityData.name} area,{subCityData.name} pet owners trust Nationwide for comprehensive coverage.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit,index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Area Information */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Pet Insurance Coverage in {subCityData.name}
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Local {subCityData.name} Areas</h3>
                  <div className="grid grid-cols-1 gap-2">
                    {subCityData.landmarks.map((landmark,index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <SafeIcon icon={FiMapPin} className="h-4 w-4 text-blue-600" />
                        <span className="text-gray-700">Near {landmark}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">What We Cover in {subCityData.name}</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">Accidents & Emergency Care</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">Illnesses & Chronic Conditions</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">Hereditary & Genetic Conditions</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500" />
                      <span className="text-gray-700">Optional Wellness Plans</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick(`${subCityData.name} Coverage Area Get Quote`)}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
                >
                  Get {subCityData.name} Quote Now
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1576201836106-db1758fd1c97?w=600&h=400&fit=crop&crop=center"
                alt={`Veterinarian examining pet in ${subCityData.name},California`}
                className="rounded-lg shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nearby Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pet Insurance in Nearby {cityData.name} Communities
            </h2>
            <p className="text-xl text-gray-600">
              We also serve these nearby communities with comprehensive pet insurance coverage
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {cityData.subCities
              .filter(sub => sub.slug !== subCitySlug)
              .slice(0,8)
              .map((nearbyCity,index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Link
                    to={`/pet-insurance/${citySlug}/${nearbyCity.slug}`}
                    className="block bg-gray-50 p-4 rounded-lg hover:shadow-md transition-shadow text-center"
                  >
                    <h3 className="font-semibold text-gray-900 mb-1">Pet Insurance {nearbyCity.name}</h3>
                    <p className="text-sm text-gray-600">Coverage for {nearbyCity.name} pets</p>
                  </Link>
                </motion.div>
              ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to={`/pet-insurance/${citySlug}`}
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <SafeIcon icon={FiArrowLeft} className="h-4 w-4 mr-2" />
              View All {cityData.name} Area Coverage
            </Link>
          </div>
        </div>
      </section>

      {/* Local CTA */}
      <section className="py-20 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Protect Your {subCityData.name} Pet?
            </h2>
            <p className="text-xl mb-8 text-blue-100">
              Join {subCityData.name} pet owners who trust America's #1 pet insurance provider for their pet's health and safety.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://partnersolutions.nationwide.com/pet/burbankinsuranceservices"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackButtonClick(`${subCityData.name} Footer Get Quote`)}
                className="bg-yellow-500 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-colors"
              >
                Get Instant {subCityData.name} Quote
              </a>
              <Link
                to="/compare"
                onClick={() => trackButtonClick(`${subCityData.name} Footer Compare Plans`)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-blue-700 transition-colors"
              >
                Compare Plans
              </Link>
            </div>
            <p className="mt-6 text-blue-200">
              Free quotes • No obligation • Instant coverage available in {subCityData.name}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
export default SubCityLandingPage;